import { useEffect, useMemo, useRef, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import EmailIcon from "../../components/icons/EmailIcon";
import ProfileImage from "../../components/profile-image/ProfileImage";
import { useAppContext } from "../../context/app-context";
import { setTokens, setUsername } from "../../utils/token-functions";
import "./my-profile.css";
import ProfileUploadIcon from "../../components/icons/ProfileUploadIcon";
import DataTable from "../../components/data-table/DataTable";
import usePagination from "../../hooks/use-pagination";
import useData from "../../hooks/use-data";
import { PaginatedApiResponseType } from "../../types/common";
import { WalletType } from "../../types/user";
import { ApiPaths } from "../../api/paths";
import { toast } from "react-toastify";
import { ActionType, ColumnType } from "../../types/data-table";
import { ConnectKitButton } from "connectkit";
import useMutateData from "../../hooks/use-mutate-data";
import { useAccount } from "wagmi";
import moment from "moment";
import CustomButton from "../../components/custom-button/CustomButton";
import { BsTrash } from "react-icons/bs";
import DeleteWalletItem from "./DeleteWalletItem/DeleteWalletItem";
import useDisclosure from "../../hooks/use-disclosure";

export default function MyProfile() {
  const {
    keycloakProfile,
    user,
    userProfilePic,
    setProfilePic,
    setProfile,
    role,
  } = useAppContext();
  const { keycloak } = useKeycloak();
  const [initialRender, setInitialRender] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const { address, isConnected, chain } = useAccount();
  const [selectedItem, setSelectedItem] = useState();
  const { onHide, onShow, show } = useDisclosure();

  const { t } = useTranslation(["myProfile", "common"]);
  const pagination = usePagination();
  const isNamedUser = !!(
    keycloakProfile?.firstName && keycloakProfile?.lastName
  );

  const profileParams = useMemo(
    () => ({
      params: {
        search: keycloakProfile?.email,
      },
    }),
    [keycloakProfile?.email]
  );

  const { data: profileData, refetch } = useData<any>(
    `${ApiPaths.profilePicture}`,
    {
      options: profileParams,
      onError: () => {
        toast(t("mutate_Profile.error.fetch_single", { ns: "myProfile" }), {
          type: "error",
        });
      },
      initialFetch: false,
      fetchIf: !!keycloakProfile?.email,
    }
  );

  useEffect(() => {
    if (profileData) {
      setProfilePic(profileData);
    }
  }, [profileData]);

  useEffect(() => {
    keycloak.loadUserProfile().then((res: any) => {
      setUsername(res?.username ?? "");
      setProfile(res);
    });
  }, []);

  const { mutate: insertProfilePic } = useMutateData(
    `${ApiPaths.profilePicture}`,
    {
      method: "POST",
      onSuccess: () => {
        toast(t("mutate_Profile.success.add", { ns: "myProfile" }), {
          type: "success",
        });
        refetch();
      },
      onError: () => {
        toast(t("mutate_Profile.error.add", { ns: "myProfile" }), {
          type: "error",
          toastId: "add-Profile",
        });
      },
    }
  );

  const { mutate: updateProfilePic } = useMutateData(
    `${ApiPaths.profilePicture}/${userProfilePic?._id}`,
    {
      method: "PUT",
      onSuccess: () => {
        toast(t("mutate_Profile.success.edit", { ns: "myProfile" }), {
          type: "success",
        });
        refetch();
      },
      onError: () => {
        toast(t("mutate_Profile.error.edit", { ns: "myProfile" }), {
          type: "error",
          toastId: "update-Profile",
        });
      },
    }
  );

  const params = useMemo(
    () => ({
      params: {
        page: pagination.page,
        limit: pagination.limit,
        email: keycloakProfile?.email,
      },
    }),
    [pagination.page, pagination.limit, keycloakProfile]
  );
  const {
    data,
    loading,
    refetch: walletRefetch,
  } = useData<PaginatedApiResponseType<WalletType[]>>(ApiPaths.wallets, {
    options: params,
    onError: () => {
      toast(t("mutate_wallet.error.fetch_single", { ns: "myProfile" }), {
        type: "error",
      });
    },
    fetchIf: !!keycloakProfile,
  });

  const { mutate: insertWalletAddress } = useMutateData(`${ApiPaths.wallets}`, {
    method: "POST",
    onSuccess: () => {
      toast(t("mutate_wallet.success.add", { ns: "myProfile" }), {
        type: "success",
      });
      walletRefetch();
    },
    onError: () => {
      toast(t("mutate_wallet.error.add", { ns: "myProfile" }), {
        type: "error",
        toastId: "add-Profile",
      });
    },
  });

  useMemo(() => {
    if (initialRender) {
      setInitialRender(false);
      return;
    }
    const walletType = chain?.name || "Unknown Wallet";
    if (isConnected && address) {
      insertWalletAddress({
        walletType,
        address,
        addedAt: moment(),
        email: keycloakProfile?.email,
      });
    }
  }, [isConnected, address]);

  const handleClickProfilePicture = () => {
    inputRef.current?.click();
  };

  const handleChangeProfilePicture = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        userProfilePic?.photo
          ? updateProfilePic({
              email: keycloakProfile?.email,
              photo: reader.result as string,
            })
          : insertProfilePic({
              email: keycloakProfile?.email,
              photo: reader.result as string,
            });
      };
      reader.readAsDataURL(file);
    }
  };

  const columns: ColumnType[] = [
    {
      accessor: "address",
      header: t("wallet.table_head.wallet_address", { ns: "myProfile" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "walletType",
      header: t("wallet.table_head.wallet_type", { ns: "myProfile" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "addedAt",
      header: t("wallet.table_head.added_at", { ns: "myProfile" }),
      formatDisplay: (row) => {
        return <div>{moment(row.addedAt).format("DD/MM/YYYY")}</div>;
      },
      headerAlignment: "center",
      alignment: "center",
    },
  ];

  const actions: ActionType[] = [
    {
      name: "remove",
      action: (row) => {
        return (
          <CustomButton
            className="btn btn-sm d-flex align-items-center btn-danger"
            onClick={() => {
              setSelectedItem(row);
              onShow();
            }}
          >
            <BsTrash className="me-1" />
            {t("button.remove", { ns: "common" })}
          </CustomButton>
        );
      },
    },
  ];

  return (
    <div className="p-3 pb-5 p-md-4">
      <DeleteWalletItem
        onHide={onHide}
        onSuccess={walletRefetch}
        selectedItem={selectedItem}
        show={show}
      />
      <h1 className="primary-title mb-3 mb-md-4">
        {t("heading", { ns: "myProfile" })}
      </h1>
      <div className="card border-0 box-shadw rounded-2 mb-3">
        <div className="position-relative">
          <img
            src={process.env.PUBLIC_URL + "/assets/icons/profile-cover.png"}
            alt="profile-cover"
            className="card-img-top"
          />
        </div>
        <div className="">
          <div className="card-body d-flex justify-content-between align-items-center p-2 p-md-4 w-100">
            <div className="d-flex align-items-center">
              <div className="me-3 me-md-5">
                <input
                  type="file"
                  ref={inputRef}
                  onChange={handleChangeProfilePicture}
                  style={{ display: "none" }}
                />
                <div className="image-container">
                  <div className="profile-picture-preview">
                    <ProfileImage
                      profileURL={userProfilePic?.photo}
                      firstName={keycloakProfile?.firstName!}
                      lastName={keycloakProfile?.lastName!}
                      size="large"
                    />
                  </div>
                  <div
                    className="upload-icon"
                    onClick={handleClickProfilePicture}
                  >
                    <ProfileUploadIcon />
                  </div>
                </div>
              </div>
              <div className="text-start">
                <div className="d-flex align-items-center">
                  <h3
                    className={`primary-title mx-4 ${
                      isNamedUser ? "mb-3 mb-md-4" : ""
                    }`}
                  >
                    {isNamedUser
                      ? keycloakProfile?.firstName ??
                        "" + " " + keycloakProfile?.lastName ??
                        ""
                      : t("label.welcome", { ns: "myProfile" })}
                  </h3>
                </div>
                {isNamedUser && (
                  <div className="d-flex flex-wrap py-2 px-2 p-md-0">
                    <div className="d-flex align-items-center me-3 me-md-5">
                      <div className="me-2 me-md-3">
                        <EmailIcon isActive={true} isLarge={true} />
                      </div>
                      <div className="">
                        <p className="email-label mb-0">
                          {t("label.email", { ns: "myProfile" })}
                        </p>
                        <p className="contact-details mb-0">
                          {keycloakProfile?.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex justify-content-end align-items-center mb-3">
              <Link
                to={`/my-profile/edit/${user?.sub}`}
                className="primary-button text-uppercase  text-decoration-none"
              >
                {t("button.edit_profile", { ns: "myProfile" })}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="box-shadow bg-white rounded-2 p-3 p-md-5 mb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="settings-title">
            {t("label.wallet", { ns: "myProfile" })}
          </h3>
          {role === "business-user" ? (
            ""
          ) : (
            <ConnectKitButton.Custom>
              {(props: any) => (
                <div>
                  <button
                    className="primary-button text-decoration-none buy-covers-button"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={props.show}
                  >
                    {props?.isConnecting
                      ? "Connecting..."
                      : t("button.add_new_wallet", { ns: "myProfile" })}
                  </button>
                </div>
              )}
            </ConnectKitButton.Custom>
          )}
        </div>
        <div className="my-cart-table mb-3">
          {role === "business-user" ? (
            <DataTable columns={columns} data={data?.data} loading={loading} />
          ) : (
            <DataTable
              columns={columns}
              data={data?.data}
              actions={actions}
              loading={loading}
            />
          )}
        </div>
      </div>
      <div className="box-shadow bg-white rounded-2 p-3 p-md-5 mb-5">
        <h3 className="settings-title mb-md-4">
          {t("label.settings", { ns: "myProfile" })}
        </h3>
        <div className="setting-options py-md-2">
          <button
            className="btn border-0 px-0"
            onClick={() =>
              keycloak.login({
                action: "UPDATE_PASSWORD",
                redirectUri: `${window.location.origin}/my-profile`,
              })
            }
          >
            {t("button.reset", { ns: "myProfile" })}
          </button>
        </div>
        <div className="py-2 py-md-4">
          <button
            className="status-button logout-button rejected-status btn px-4 py-1"
            onClick={() => {
              setTokens("", "");
              keycloak.logout();
            }}
          >
            {t("button.logout", { ns: "common" })}
          </button>
        </div>
      </div>
    </div>
  );
}
